<template>
  <v-menu v-if="authStore.isAuthenticated" location="bottom end">
    <template v-slot:activator="{ props }">
      <v-btn v-bind="props" variant="text" class="user-menu-btn">
        <div class="user-avatar">{{ initials }}</div>
        <div class="user-info d-none d-md-block">
          <div class="user-name">{{ userName }}</div>
          <div class="user-role">{{ roleDisplay }}</div>
        </div>
        <v-icon icon="mdi-chevron-down" class="ml-1" size="small" />
      </v-btn>
    </template>

    <v-list min-width="200">
      <v-list-item>
        <v-list-item-title class="font-weight-medium">{{ userName }}</v-list-item-title>
        <v-list-item-subtitle>{{ userEmail }}</v-list-item-subtitle>
      </v-list-item>

      <v-divider />

      <v-list-item to="/" prepend-icon="mdi-view-dashboard">
        <v-list-item-title>Dashboard</v-list-item-title>
      </v-list-item>

      <!-- Per-app NMA menu items injected via slot -->
      <slot name="nma-menu-items" />

      <v-divider />

      <v-list-item @click="handleLogout" prepend-icon="mdi-logout" color="error">
        <v-list-item-title>Sign Out</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>

  <v-btn v-else to="/login" color="white" variant="outlined" size="small"> Sign In </v-btn>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'

const router = useRouter()
const authStore = useAuthStore()

const userName = computed(() => {
  const user = authStore.user
  if (!user) return ''
  return `${user.firstName || ''} ${user.lastName || ''}`.trim()
})

const userEmail = computed(() => authStore.user?.email || '')

const initials = computed(() => {
  const user = authStore.user
  if (!user) return ''
  const first = user.firstName?.charAt(0) || ''
  const last = user.lastName?.charAt(0) || ''
  return (first + last).toUpperCase()
})

const roleDisplay = computed(() => {
  const user = authStore.user
  if (!user) return ''
  if (user.userType === 'nma') {
    return `NMA ${user.nmaRole || 'Staff'}`
  }
  return (
    user.companies?.find((c: { primary?: boolean; name: string }) => c.primary)?.name ||
    'External User'
  )
})

async function handleLogout() {
  try {
    await authStore.logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout failed:', error)
    // Force redirect to login even if logout fails
    router.push('/login')
  }
}
</script>

<style scoped>
.user-menu-btn {
  text-transform: none;
  color: white;
  gap: 8px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.user-info {
  text-align: left;
}

.user-name {
  color: white;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
}

.user-role {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  line-height: 1.2;
}
</style>
